import type { MouseEvent } from 'react';
import styled from 'styled-components';
import type {
  BannerMediaPlacement,
  ButtonVariation,
  DoubleBannerDescriptions,
  MediaFit,
} from '@amplience/content-types/typings/c-double-banner';
import { useMediaMatch } from '@storefront/lib-global/hooks/useMediaMatch';
import { FlexibleTextPartial } from '@storefront/lib-global/partials/flexibleText';
import { LinkPartial } from '@storefront/lib-global/partials/link';
import type { MediaPartialProps } from '@storefront/lib-global/partials/media';
import { MediaPartial } from '@storefront/lib-global/partials/media';
import { RichTextPartial } from '@storefront/lib-global/partials/richText';
import { TypographyStyles, TypographyStylesType, colours, media, spacing } from '@storefront/lib-global/stylings';
import { sendSelectContentEvent } from '@storefront/lib-global/utils/gtm/events/select_content/sendSelectContentEvent';
import { getLocalizedValue } from '@storefront/lib-global/utils/transformers';
import { BannerButtonStyles, getOverlappingBannerStyles } from '../../utils';
import TwoColumnBanner from './TwoColumnBanner';
import type { DoubleBannerProps } from './bannerType';

type MultipleOverlappingBannerProps = DoubleBannerProps;

const S = {
  Button: styled(LinkPartial)<{ $variation?: ButtonVariation }>`
    ${({ $variation }) => BannerButtonStyles($variation)}

    @media ${media.greaterThan('lg')} {
      max-width: 340px;
    }
  `,

  Buttons: styled.ul`
    margin: 0;
    padding: 0;

    & li:not(:last-child) {
      margin-bottom: ${spacing.XXS};
    }
  `,

  Container: styled.div<{ $bannerMediaPosition?: BannerMediaPlacement; $mediaLen?: number }>`
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: 48px;
    margin-inline: -${spacing.S};

    @media ${media.greaterThan('lg')} {
      flex-direction: ${({ $bannerMediaPosition }) => ($bannerMediaPosition === 'Image Left' ? 'row' : 'row-reverse')};
      margin-inline: 0;

      > * {
        flex: 1;
      }
    }

    @media ${media.greaterThan('xl')} {
      padding-block: ${({ $mediaLen = 3 }) => $mediaLen === 2 && '70px'};
    }
  `,

  Details: styled.div<{ $textWidth?: string }>`
    margin: ${spacing.S} 0;
    padding-inline: ${spacing.S};

    p {
      margin: 0;
    }

    > *:not(:last-child) {
      margin-bottom: ${spacing.XS};
    }

    @media ${media.greaterThan('lg')} {
      flex-basis: ${({ $textWidth = '50%' }) => $textWidth};
      padding: ${spacing.S} 0;
    }
  `,

  Media: styled.div<{ $mediaFit?: MediaFit }>`
    position: relative;

    video {
      height: 100%;
      object-fit: ${({ $mediaFit = 'cover' }) => $mediaFit};
      width: 100%;
    }
  `,

  Medias: styled.div<{ $bannerMediaPosition?: BannerMediaPlacement; $mediaLen?: number; $textWidth?: string }>`
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    grid-template-rows: repeat(12, 1fr);
    overflow-x: clip;
    position: relative;
    width: 100%;

    ${({ $mediaLen = 3, $bannerMediaPosition = 'Image Left' }) =>
      getOverlappingBannerStyles($mediaLen, $bannerMediaPosition)}

    @media ${media.greaterThan('lg')} {
      flex-basis: ${({ $textWidth = '50%' }) => `calc(100% - ${$textWidth})`};
    }
  `,

  OverlappingBannerDescription: styled(RichTextPartial)<{ $textColor?: string }>`
    color: ${({ $textColor }) => $textColor};
    overflow-wrap: break-word;
    width: 100%;

    & > p {
      margin: 0;
    }

    & a {
      color: ${({ $textColor }) => $textColor};
    }

    @media ${media.greaterThan('lg')} {
      margin: 0;
    }
  `,
};

export function MultipleOverlappingBanner(props: MultipleOverlappingBannerProps) {
  const { bannerTitle, bannerDescriptions, bannerMedia, bannerMediaPosition, bannerButtons, textWidth, onClick } =
    props;
  const isDesktop = useMediaMatch(media.greaterThan('lg'));
  const currentMedias = isDesktop ? bannerMedia?.desktopMedia : bannerMedia?.mobileMedia;

  if (currentMedias?.length === 1) return <TwoColumnBanner {...props} />;

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    const element = event.currentTarget as HTMLAnchorElement;

    sendSelectContentEvent({
      component_type: 'Multiple Overlapping Banner',
      component_header: getLocalizedValue(bannerTitle?.text ?? '').trim(),
      link_url: element.href,
      link_text: element.textContent ?? '',
    });
  };

  return (
    <S.Container $bannerMediaPosition={bannerMediaPosition} $mediaLen={currentMedias?.length}>
      <S.Medias $bannerMediaPosition={bannerMediaPosition} $mediaLen={currentMedias?.length} $textWidth={textWidth}>
        {currentMedias?.map((currentMedia, index) => (
          <S.Media key={`${currentMedia?.media?.name}-${index}`} $mediaFit={currentMedia?.mediaFit}>
            <MediaPartial
              {...(currentMedia as MediaPartialProps)}
              height={100}
              width={100}
              isImageLayoutFill
              mediaFit={currentMedia?.mediaFit}
              videoQuality={currentMedia?.videoQuality}
              altText={currentMedia?.altText}
              autoPlay={false}
              isMediaFocusable={false}
            />
          </S.Media>
        ))}
      </S.Medias>
      <S.Details $textWidth={textWidth}>
        {!!bannerTitle?.text && (
          <FlexibleTextPartial
            {...bannerTitle}
            defaultTextSize={TypographyStylesType.HEADINGS_H5}
            defaultTextColor={colours.BLACK}
          />
        )}
        {(bannerDescriptions as DoubleBannerDescriptions[] | undefined)?.map((description, index) => (
          <S.OverlappingBannerDescription
            key={`${description?.richText}-${index}`}
            text={getLocalizedValue(description?.richText)}
            fontSize={TypographyStyles.Body.Medium.Regular}
            $textColor={description?.textColor as string}
          />
        ))}
        {!!bannerButtons?.length && (
          <S.Buttons>
            {bannerButtons?.slice(0, 4).map((button, index) => (
              <li key={`${button.buttonText}-${index}`}>
                <S.Button $variation={button.buttonVariation} {...button.link} onClick={handleClick}>
                  {getLocalizedValue(button.buttonText)}
                </S.Button>
              </li>
            ))}
          </S.Buttons>
        )}
      </S.Details>
    </S.Container>
  );
}

export default MultipleOverlappingBanner;
